import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getCurrentProject, getCurrentUserAssociation } from '../modules/project';
import { useUser } from './UserContext';
import {
  buildPermissionSet,
  checkPermission,
  checkAnyPermission,
  checkAllPermissions,
} from '../lib/permissions';

const ProjectContext = createContext(null);

const normalizePermissions = (permissions = []) =>
  [...new Set(permissions.filter(Boolean))];

const getEffectiveProjectAccess = (association, orgRole, orgPermissions = []) => {
  const hasOrgPermissions =
    Array.isArray(orgPermissions) && orgPermissions.length > 0;
  const hasProjectPermissions =
    Array.isArray(association?.permissions) && association.permissions.length > 0;

  if (hasOrgPermissions && orgRole !== 'org-member' && !hasProjectPermissions) {
    return {
      role: orgRole ?? null,
      permissions: normalizePermissions(orgPermissions),
    };
  }

  return {
    role: association?.role ?? null,
    permissions: normalizePermissions(association?.permissions ?? []),
  };
};

export const ProjectProvider = ({ children }) => {
  const { projectId: routeParamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { role: orgRole, userPermissions: orgPermissions } = useUser();
  const orgRoleRef = useRef(orgRole);
  const orgPermissionsRef = useRef(orgPermissions);

  const [currentProject, setCurrentProject] = useState(null);
  const [projectUser, setProjectUser] = useState(null);
  const [projectRole, setProjectRole] = useState(null);
  const [projectPermissions, setProjectPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);

  const projectId = useMemo(() => {
    if (routeParamId) return routeParamId;
    const match = location.pathname.match(/\/project\/([^/]+)/);
    return match ? match[1] : null;
  }, [routeParamId, location.pathname]);

  useEffect(() => {
    orgRoleRef.current = orgRole;
    orgPermissionsRef.current = orgPermissions;
  }, [orgRole, orgPermissions]);

  const resetProjectState = useCallback(() => {
    setCurrentProject(null);
    setProjectUser(null);
    setProjectRole(null);
    setProjectPermissions([]);
  }, []);

  const applyEffectiveProjectAccess = useCallback((association, role, permissions) => {
    const effectiveAccess = getEffectiveProjectAccess(association, role, permissions);
    setProjectRole(effectiveAccess.role);
    setProjectPermissions(effectiveAccess.permissions);
  }, []);

  const refreshProject = useCallback(async () => {
    if (!projectId) {
      resetProjectState();
      setError(null);
      setIsReady(true);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [association, project] = await Promise.all([
        getCurrentUserAssociation(projectId),
        getCurrentProject(projectId),
      ]);

      setProjectUser(association);
      applyEffectiveProjectAccess(
        association,
        orgRoleRef.current,
        orgPermissionsRef.current
      );
      setCurrentProject(project);
      return { association, project };
    } catch (err) {
      resetProjectState();
      setError(err);
      navigate('/access-denied', { replace: true });
      return null;
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [projectId, resetProjectState, navigate, applyEffectiveProjectAccess]);

  useEffect(() => {
    setIsReady(false);
    refreshProject().catch(console.error);
  }, [refreshProject]);

  useEffect(() => {
    if (!projectId || !isReady || isLoading) return;

    applyEffectiveProjectAccess(projectUser, orgRole, orgPermissions);
  }, [
    projectId,
    isReady,
    isLoading,
    projectUser,
    orgRole,
    orgPermissions,
    applyEffectiveProjectAccess,
  ]);

  const permissionSet = useMemo(
    () => buildPermissionSet(projectPermissions),
    [projectPermissions]
  );

  const can = useCallback(
    (permission) => checkPermission(permissionSet, permission),
    [permissionSet]
  );

  const hasAny = useCallback(
    (permissions) => checkAnyPermission(permissionSet, permissions),
    [permissionSet]
  );

  const hasAll = useCallback(
    (permissions) => checkAllPermissions(permissionSet, permissions),
    [permissionSet]
  );

  const value = useMemo(() => ({
    projectId,
    currentProject,
    projectUser,
    projectRole,
    projectPermissions,
    permissionSet,
    can,
    hasAny,
    hasAll,
    isLoading,
    isReady,
    error,
    refreshProject,
    refreshProjectUser: refreshProject,
  }), [
    projectId,
    currentProject,
    projectUser,
    projectRole,
    projectPermissions,
    permissionSet,
    can,
    hasAny,
    hasAll,
    isLoading,
    isReady,
    error,
    refreshProject,
  ]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within a ProjectProvider');
  return context;
};

export const useProjectOptional = () => useContext(ProjectContext);